/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Generate home summary facts for "Based on your home..." notes
 * 
 * These labels help the presentation layer generate natural language
 * summaries about the user's home, emitters, and low-temp readiness.
 */

import type {
  RetrofitLevel,
  LowTempReadiness,
  EmitterProfile,
  RetrofitProfile,
} from './types';

export interface HomeSummaryFacts {
  retrofitLevelLabel: string;
  emitterTypeLabel: string;
  lowTempReadinessLabel: string;
  assumptionsUsed: string[];
}

/**
 * Generate friendly label for retrofit level
 */
export function getRetrofitLevelLabel(level: RetrofitLevel): string {
  switch (level) {
    case 'high':
      return 'well-insulated';
    case 'medium':
      return 'typical insulation upgrades';
    case 'low':
      return 'limited insulation';
    default:
      return 'unknown insulation';
  }
}

/**
 * Generate friendly label for emitter type
 */
export function getEmitterTypeLabel(profile: EmitterProfile): string {
  switch (profile.type) {
    case 'radiators':
      return 'radiators';
    case 'underfloor':
      return 'underfloor heating';
    case 'mixed':
      return 'mix of radiators and underfloor heating';
    case 'unknown':
    default:
      return 'heating emitters';
  }
}

/**
 * Generate friendly label for low-temp readiness
 */
export function getLowTempReadinessLabel(readiness: LowTempReadiness): string {
  switch (readiness) {
    case 'high':
      return 'well-suited for low-temperature heating';
    case 'medium':
      return 'moderately suited for low-temperature heating';
    case 'low':
      return 'may need upgrades for low-temperature heating';
    default:
      return 'unknown suitability for low-temperature heating';
  }
}

/**
 * Generate list of assumptions used (fields that were defaulted to "unknown")
 */
export function getAssumptionsUsed(
  emitterProfile: EmitterProfile,
  retrofitProfile: RetrofitProfile
): string[] {
  const assumptions: string[] = [];

  // Check emitter assumptions
  if (emitterProfile.type === 'unknown') {
    assumptions.push('assumed typical radiators for emitter type');
  }
  if (emitterProfile.lowTempSuitability === 'unknown') {
    // Make message conditional on emitter type
    if (emitterProfile.type === 'radiators') {
      assumptions.push('assumed medium low-temp suitability for radiators');
    } else if (emitterProfile.type === 'underfloor') {
      assumptions.push('assumed good low-temp suitability for underfloor heating');
    } else if (emitterProfile.type === 'mixed') {
      assumptions.push('assumed medium low-temp suitability for mixed emitters');
    } else {
      assumptions.push('assumed medium low-temp suitability for heating emitters');
    }
  }
  if (emitterProfile.roomsStruggleToHeat === 'unknown') {
    assumptions.push('assumed no significant heating struggles');
  }

  // Check retrofit assumptions
  if (retrofitProfile.loftInsulation === 'unknown') {
    assumptions.push('assumed medium loft insulation');
  }
  if (retrofitProfile.wallInsulation === 'unknown') {
    assumptions.push('assumed medium wall insulation');
  }
  if (retrofitProfile.windows === 'unknown') {
    assumptions.push('assumed typical double glazing');
  }
  if (retrofitProfile.draughtProofing === 'unknown') {
    assumptions.push('assumed medium draught proofing');
  }

  return assumptions;
}

/**
 * Generate all home summary facts
 */
export function generateHomeSummaryFacts(
  retrofitLevel: RetrofitLevel,
  lowTempReadiness: LowTempReadiness,
  emitterProfile: EmitterProfile,
  retrofitProfile: RetrofitProfile
): HomeSummaryFacts {
  return {
    retrofitLevelLabel: getRetrofitLevelLabel(retrofitLevel),
    emitterTypeLabel: getEmitterTypeLabel(emitterProfile),
    lowTempReadinessLabel: getLowTempReadinessLabel(lowTempReadiness),
    assumptionsUsed: getAssumptionsUsed(emitterProfile, retrofitProfile),
  };
}
